'use client'

import { useEffect, useState } from 'react'
import { appWithTranslation } from 'next-i18next/pages'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SessionProvider, useSession } from '../src/context/SessionProvider'
import { SystemProvider } from '../src/context/SystemProvider'
import { ModalProvider } from '../src/context/ModalProvider'
import { Buffer } from 'buffer'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Global } from '@emotion/react'
import { PostHogProvider } from 'posthog-js/react'
import '@fontsource/poppins/100.css'
import '@fontsource/poppins/200.css'
import '@fontsource/poppins/300.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
import '@fontsource/poppins/800.css'
import '@fontsource/poppins/900.css'
import '@fontsource-variable/wix-madefor-text'
import Head from 'next/head'

import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client'
import { AccountProvider } from '@/context/AccountProvider'
import { INDEXER } from '../src/constants/constants'
import { AudioProvider } from '../src/context/AudioProvider'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { ChakraProvider } from '@chakra-ui/react'
import theme from '@/theme'
import { AppProps } from 'next/app'
import { Layout } from '@/components/Layouts/Layout'
import { relayStylePagination } from '@apollo/client/utilities'

import nextI18NextConfig from '../next-i18next.config.js'
import { GoogleTagManager } from '@next/third-parties/google'

import { ThirdwebProvider } from 'thirdweb/react'
import posthog from 'posthog-js'
import { getAnonymousUserName } from '@/helpers/getAnonymousUserName'
import { UserBalanceProvider } from '@/context/UserBalance'
import { FiatCurrencyProvider } from '@/context/FiatCurrencyProvider'

import { IntercomProvider } from '@/context/Intercom'
import socketIO from '@/services/socket'
import { LocalStorageKey } from '@/constants/localstorage'
import { EnteringMode, enteringModeResolver } from '@/hooks/useEnteringModeValue'
import { useLocalStorage } from 'usehooks-ts'
import { ChatwootWidget } from '@/components/organisms/ChatwootWidget'
import { useTranslation } from 'next-i18next/pages'
import { UsdcConversionChecker } from '@/components/molecules/UsdcConversionModal/UsdcConversionChecker'

const queryClient = new QueryClient()

const client = new ApolloClient({
  link: new BatchHttpLink({
    uri: INDEXER,
    batchMax: 100, // No more than 100 operations per batch
    batchInterval: 20, // Wait no more than 20ms after first batched operation
  }),
  cache: new InMemoryCache({
    typePolicies: {
      lootboxGames: {
        keyFields: [['lootbox', 'id']],
      },
      User: {
        fields: {
          profileUserName: {
            read(name, c) {
              if (name.startsWith('inj1') || name.startsWith('Ninja-')) {
                return getAnonymousUserName(c.variables!.address)
              }
              return name
            },
          },
        },
      },
      Query: {
        fields: {
          jackpotUserGames: relayStylePagination(['player']),
          doubleUserGames: relayStylePagination(['filter']),
          minesGames: relayStylePagination(['filter']),
          betsHistories: relayStylePagination(['filter']),
          user: {
            merge: true,
          },
        },
      },
    },
  }),
})

function App({ Component, pageProps }: AppProps) {
  const [isMounted, setIsMounted] = useState(false)
  const { i18n } = useTranslation()

  useEffect(() => {
    const language = nextI18NextConfig.i18n.defaultLocale

    if (i18n.language !== language) {
      i18n.changeLanguage(language)
    }

    localStorage.setItem(LocalStorageKey.Language, language)
  }, [i18n])

  useEffect(() => {
    window.Buffer = Buffer

    if (window.location.hostname === 'app.blaze.ninja') {
      if (process.env.NEXT_PUBLIC_APP_POSTHOG_KEY) {
        posthog.init(process.env.NEXT_PUBLIC_APP_POSTHOG_KEY, {
          api_host: `https://app.blaze.ninja/ingest`,
          person_profiles: 'identified_only',
          session_recording: {
            maskAllInputs: false,
          },
        })
      }
      setIsMounted(true)
    }
  }, [])

  useEffect(() => {
    socketIO.connect()

    return () => {
      socketIO.disconnect()
    }
  }, [])

  const params = useSearchParams()
  const router = useRouter()
  const { signIn } = useSession()

  const [_, setEnteringMode] = useLocalStorage(LocalStorageKey.EnteringMode, EnteringMode.Unknown)

  useEffect(() => {
    const mode = params.get('mode')

    if (!mode) {
      return
    }

    router.replace('/', {
      scroll: false,
    })

    const parsedMode = enteringModeResolver(mode)
    setEnteringMode(parsedMode)

    if (!useSession.getState().account && parsedMode === EnteringMode.Login) {
      setTimeout(() => signIn(), 1000)
    }
  }, [params])

  // useEffect(() => {
  //   const handleRouteChange = async () => {
  //     if (window.location.hostname !== 'app.blaze.ninja') {
  //       return
  //     }
  //     const versionCheck = JSON.parse(localStorage.getItem('version-check') || '{}')

  //     if (Number(versionCheck.lastUpdate) < new Date().getTime() - 5 * 60 * 1000) {
  //       versionCheck.lastUpdate = new Date().getTime()
  //       localStorage.setItem('version-check', JSON.stringify(versionCheck))

  //       versionCheck.lastVersion = await fetch('/version.txt').then((res) => res.text())
  //       localStorage.setItem('version-check', JSON.stringify(versionCheck))
  //     }

  //     if (process.env.NEXT_PUBLIC_VERSION == versionCheck.lastVersion) {
  //       return
  //     }

  //     window.location.reload()
  //   }

  //   router.events.on('routeChangeStart', handleRouteChange)

  //   window.addEventListener('visibilitychange', handleRouteChange, false)
  //   window.addEventListener('focus', handleRouteChange, false)

  //   return () => {
  //     router.events.off('routeChangeStart', handleRouteChange)

  //     window.removeEventListener('visibilitychange', handleRouteChange)
  //     window.removeEventListener('focus', handleRouteChange)
  //   }
  // }, [router])

  return (
    <>
      <HeadMetadata />
      {isMounted && <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID!} />}
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider client={client}>
            <ChakraProvider theme={theme}>
              <ThirdwebProvider>
                <SessionProvider />
                <AccountProvider>
                  <AudioProvider>
                    <UserBalanceProvider />
                    <FiatCurrencyProvider />
                    <UsdcConversionChecker />
                    <ScrollToTop />
                    <Globals />
                    <ModalProvider />
                    <SystemProvider />
                    <ChatwootWidget />
                    <IntercomProvider />
                    <Layout>
                      <Component {...pageProps} />
                    </Layout>
                  </AudioProvider>
                </AccountProvider>
              </ThirdwebProvider>
            </ChakraProvider>
          </ApolloProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </>
  )
}

export default appWithTranslation(App, nextI18NextConfig)

function ScrollToTop() {
  const pathname = usePathname()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const Globals = () => (
  <Global
    styles={`
    #chakra-toast-manager-top-right {
      margin-top: 64px;
    }

    .grid {
      display: grid;
    }

    .none {
      display: none;
    }

    .wallet-adapter-modal {
      z-index: 2000;
    }

    .lootbox-rolling-track-item {
      display: flex;
      border-radius: 20px;
      border: 2px solid;
      height: 158px;
      width: 158px;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      flex-direction: column
    }

    .lootbox-rolling-track-item img {
      width: 72px;
      height: 72px;
    }
    
    img {
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
        user-drag: none;
        -webkit-touch-callout: none;
      }

    *::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    *::-webkit-scrollbar-track {
      background: transparent;
    }

    *::-webkit-scrollbar-thumb {
      background: #15143d;
      border-radius: 24px;
    }

    *::-webkit-scrollbar-thumb:hover {
      background: #1d1c49;
    }
  `}
  />
)

const HeadMetadata = () => {
  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1" viewport-fit="cover" />
      <title>Ninja Blaze - Blockchain online games</title>
      <meta
        name="description"
        key={'description'}
        content="Transforming the landscape of crypto-based gaming. Brace for a decentralized gaming revolution!"
      />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="Ninja Blaze - Blockchain online games" />
      <meta
        property="og:description"
        key={'og:description'}
        content="Transforming the landscape of crypto-based gaming. Brace for a decentralized gaming revolution!"
      />
      <meta property="og:image" key={'og:image'} content="https://app.blaze.ninja/assets/images/og_banner.png" />
      <meta property="og:url" content="https://blaze.ninja" />
      <meta property="og:site_name" content="Blaze.ninja" />

      <meta name="twitter:site" content="@NinjaBlazeApp" />
      <meta name="twitter:title" content="Ninja Blaze - Blockchain online games" />
      <meta
        name="twitter:description"
        key={'twitter:description'}
        content="Transforming the landscape of crypto-based gaming. Brace for a decentralized gaming revolution!"
      />
      <meta name="twitter:url" content="https://blaze.ninja" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" key={'twitter:image'} content="https://app.blaze.ninja/assets/images/og_banner.png" />
      <meta name="twitter:image:alt" content="Blaze ninja banner" />
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="manifest" href="/manifest.json" />
    </Head>
  )
}
